import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { userService, roleService } from '@/lib/api/services'
import type { UserOData } from '@/lib/api/services/users'
import type { RoleOData } from '@/lib/api/services/roles'
import type { ApproverType, AddStageApproverRequest } from '@/lib/api/types'
import {
  Search,
  Loader2,
  Users,
  UserCircle,
  Building2,
  Check,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ApproverSelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (approver: AddStageApproverRequest & { approverName: string }) => void
  existingApproverIds?: string[]
}

interface SelectableItem {
  id: string
  name: string
  type: ApproverType
  description?: string
}

export function ApproverSelector({
  open,
  onOpenChange,
  onSelect,
  existingApproverIds = [],
}: ApproverSelectorProps) {
  const [activeTab, setActiveTab] = useState<ApproverType>('User')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<UserOData[]>([])
  const [roles, setRoles] = useState<RoleOData[]>([])
  const [teams, setTeams] = useState<SelectableItem[]>([])
  const [selectedItem, setSelectedItem] = useState<SelectableItem | null>(null)
  const [permissions, setPermissions] = useState({
    canApprove: true,
    canReject: true,
    canRequestChanges: true,
  })

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        userService.getUsers().catch(() => ({ value: [] })),
        roleService.getRoles().catch(() => ({ value: [] })),
      ])

      setUsers(usersResponse.value || [])
      setRoles(rolesResponse.value || [])
      // Teams would come from a teams service - mocking for now
      setTeams([
        { id: 'team-1', name: 'Marketing Team', type: 'Team', description: '5 members' },
        { id: 'team-2', name: 'Design Team', type: 'Team', description: '3 members' },
        { id: 'team-3', name: 'Legal Team', type: 'Team', description: '2 members' },
      ])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      loadData()
      setSelectedItem(null)
      setSearch('')
      setPermissions({
        canApprove: true,
        canReject: true,
        canRequestChanges: true,
      })
    }
  }, [open, loadData])

  const getFilteredItems = (): SelectableItem[] => {
    const searchLower = search.toLowerCase()

    switch (activeTab) {
      case 'User':
        return users
          .filter(
            (user) =>
              !existingApproverIds.includes(user.id) &&
              (user.fullName.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower))
          )
          .map((user) => ({
            id: user.id,
            name: user.fullName,
            type: 'User' as ApproverType,
            description: user.email,
          }))
      case 'Role':
        return roles
          .filter(
            (role) =>
              !existingApproverIds.includes(role.id) &&
              role.name.toLowerCase().includes(searchLower)
          )
          .map((role) => ({
            id: role.id,
            name: role.name,
            type: 'Role' as ApproverType,
            description: role.description,
          }))
      case 'Team':
        return teams.filter(
          (team) =>
            !existingApproverIds.includes(team.id) &&
            team.name.toLowerCase().includes(searchLower)
        )
      default:
        return []
    }
  }

  const handleSelect = () => {
    if (!selectedItem) return

    onSelect({
      approverType: selectedItem.type,
      approverId: selectedItem.id,
      approverName: selectedItem.name,
      ...permissions,
    })
    onOpenChange(false)
  }

  const getIcon = (type: ApproverType) => {
    switch (type) {
      case 'User':
        return <UserCircle className="h-5 w-5" />
      case 'Role':
        return <Users className="h-5 w-5" />
      case 'Team':
        return <Building2 className="h-5 w-5" />
    }
  }

  const filteredItems = getFilteredItems()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Approver</DialogTitle>
          <DialogDescription>
            Select a user, role, or team to add as an approver for this stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tabs for User/Role/Team */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ApproverType)}>
            <TabsList className="w-full">
              <TabsTrigger value="User" className="flex-1">
                <UserCircle className="h-4 w-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="Role" className="flex-1">
                <Users className="h-4 w-4 mr-2" />
                Roles
              </TabsTrigger>
              <TabsTrigger value="Team" className="flex-1">
                <Building2 className="h-4 w-4 mr-2" />
                Teams
              </TabsTrigger>
            </TabsList>

            {/* Search Input */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                placeholder={`Search ${activeTab.toLowerCase()}s...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Content */}
            <TabsContent value={activeTab} className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                </div>
              ) : error ? (
                <div className="flex items-center gap-2 p-4 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm">{error}</span>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                  <p className="text-sm">
                    {search
                      ? `No ${activeTab.toLowerCase()}s found matching "${search}"`
                      : `No ${activeTab.toLowerCase()}s available`}
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {filteredItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedItem(item)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
                          selectedItem?.id === item.id
                            ? 'bg-neutral-100 dark:bg-neutral-800'
                            : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                        )}
                      >
                        <div
                          className={cn(
                            'p-2 rounded-full',
                            selectedItem?.id === item.id
                              ? 'bg-neutral-200 dark:bg-neutral-700'
                              : 'bg-neutral-100 dark:bg-neutral-800'
                          )}
                        >
                          {getIcon(item.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                            {item.name}
                          </p>
                          {item.description && (
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                              {item.description}
                            </p>
                          )}
                        </div>
                        {selectedItem?.id === item.id && (
                          <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
          </Tabs>

          {/* Permissions */}
          {selectedItem && (
            <div className="space-y-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <Label>Permissions</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="canApprove"
                    checked={permissions.canApprove}
                    onCheckedChange={(checked) =>
                      setPermissions({ ...permissions, canApprove: checked as boolean })
                    }
                  />
                  <Label htmlFor="canApprove" className="text-sm font-normal cursor-pointer">
                    Can approve
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="canReject"
                    checked={permissions.canReject}
                    onCheckedChange={(checked) =>
                      setPermissions({ ...permissions, canReject: checked as boolean })
                    }
                  />
                  <Label htmlFor="canReject" className="text-sm font-normal cursor-pointer">
                    Can reject
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="canRequestChanges"
                    checked={permissions.canRequestChanges}
                    onCheckedChange={(checked) =>
                      setPermissions({ ...permissions, canRequestChanges: checked as boolean })
                    }
                  />
                  <Label
                    htmlFor="canRequestChanges"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Can request changes
                  </Label>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!selectedItem}>
            Add Approver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
